import styled from 'styled-components';

/* Kolor marki Stripe. Karta podpiętego konta i przycisk łączenia używają go świadomie:
   to jest moment, w którym hotelarz oddaje dane finansowe zewnętrznej firmie i powinien
   od razu widzieć, KOMU. Reszta ekranu zostaje w kolorystyce panelu. */
const STRIPE_PURPLE = '#635BFF';

/* Sekcja ma wyglądać jak natywna część strony ustawień outletu, a każda z tych stron
   trzyma własne, lokalne `Section`/`SectionTitle`/`SectionDescription`. Powtarzamy tu
   ich wygląd — tak samo jak `OutletCurrencySection`, z tego samego powodu. */

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SectionHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SectionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 18px;
  background: ${({ theme }) => (theme.mode === 'dark' ? theme.colors.background.card : '#ffffff')};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
`;

/**
 * Wariant osadzony: ten sam środek, ale bez kreski i własnego tytułu. Używa go moduł,
 * który ma już swoją sekcję o płatnościach (room service: „Sposoby płatności") — dwa
 * nagłówki i kreska między nimi robiły z jednego tematu dwa osobne bloki.
 */
export const Embedded = styled.div`
  margin-top: 12px;

  /* Pierwszy element w środku nie dokłada własnego odstępu: rząd akcji ma margines
     policzony pod sekcję z nagłówkiem, a tutaj sumował się z odstępem rodzica i sam
     przycisk „Połącz ze Stripe" odjeżdżał od listy metod dalej, niż do niej należy. */
  > *:first-child {
    margin-top: 0;
  }
`;

export const SectionTitle = styled.h3`
  margin: 0;
  padding: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f9fafb' : '#111827')};
  border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#4b5563')};
`;

export const SectionDescription = styled.p`
  margin: 0;
  padding: 0;
  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Card = styled.div`
  padding: 16px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(99, 91, 255, 0.35)' : 'rgba(99, 91, 255, 0.25)')};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(99, 91, 255, 0.08)' : 'rgba(99, 91, 255, 0.04)')};
  border-radius: 6px;
  margin-bottom: 12px;
`;

/** Znak Stripe'a w kwadracie — kotwica wizualna karty. */
export const BrandMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 9px;
  color: #ffffff;
  background: ${STRIPE_PURPLE};

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const CardIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const CardRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const AccountName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#1f2937')};
`;

export const AccountMeta = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  margin-top: 2px;
`;

export const Status = styled.span<{ $tone: 'ok' | 'pending' | 'off' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;

  color: ${({ $tone }) => {
    if ($tone === 'ok') return '#047857';
    if ($tone === 'pending') return '#b45309';
    return '#4b5563';
  }};

  background: ${({ $tone }) => {
    if ($tone === 'ok') return 'rgba(16, 185, 129, 0.12)';
    if ($tone === 'pending') return 'rgba(217, 119, 6, 0.12)';
    return 'rgba(107, 114, 128, 0.12)';
  }};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

export const RequirementsTitle = styled.p`
  font-size: 12px;
  font-weight: 500;
  margin: 14px 0 0;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#fcd34d' : '#92400e')};
`;

export const Requirements = styled.ul`
  margin: 12px 0 0;
  padding-left: 18px;
  font-size: 12px;
  line-height: 1.6;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#fcd34d' : '#92400e')};
`;

export const FeeLine = styled.p`
  font-size: 13px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#d1d5db' : '#374151')};
  margin: 14px 0 0;
`;

export const FeeHint = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  margin-top: 2px;
`;

/**
 * Miejsce na ustawienie modułu, które należy do TEJ SAMEJ decyzji co konto Stripe
 * (w spa: „czy pobierać płatność z góry"). Osobna sekcja obok robiła z jednej rzeczy dwie
 * i kazała czytać dwa razy to samo, więc trzymamy je pod wspólnym nagłówkiem, oddzielone
 * kreską zamiast nowym tytułem.
 */
export const ExtraSetting = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#f1f5f9')};
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #ffffff;
  background: ${STRIPE_PURPLE};
  transition: opacity 0.15s ease;

  svg {
    width: 15px;
    height: 15px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  background: transparent;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#d1d5db' : '#374151')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  transition: border-color 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#d1d5db')};
  }
`;

export const ExternalLink = styled.a`
  font-size: 13px;
  font-weight: 500;
  color: ${STRIPE_PURPLE};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const Muted = styled.p`
  font-size: 13px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  margin: 0;
`;

export const SoftNotice = styled.p`
  margin: 8px 0 0;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.4;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(245,158,11,0.35)' : '#fde68a')};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(245,158,11,0.08)' : '#fffbeb')};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#fbbf24' : '#92400e')};
`;

export const SoftNoticeLink = styled.a`
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  color: inherit;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }
`;

export const AccountActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

/**
 * Akcje na kafelku konta to LINKI, nie przyciski — wybór konta jest nawigacją po
 * liście, a dwa obwiedzione prostokąty przy każdej pozycji przeciążały kartę.
 */
export const LinkAction = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.primary};

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const DangerLinkAction = styled(LinkAction)`
  color: #dc2626;
`;
